import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useAuthContext } from "@/contexts/AuthContext";

export interface SavedQRCode {
  id: string;
  name: string;
  description?: string;
  type: string;
  shortCode: string;
  targetUrl: string;
  fgColor: string;
  bgColor: string;
  size: number;
  errorLevel: "L" | "M" | "Q" | "H";
  template?: string;
  qrName?: string;
  qrSubtext?: string;
  brandText?: string;
  createdAt: string;
  updatedAt: string;
  scans: number;
  userId: string;
}

function generateShortCode(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
  let result = "";
  for (let i = 0; i < 6; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

function toCamelCase(row: Record<string, unknown>): SavedQRCode {
  return {
    id: row.id as string,
    name: row.name as string,
    description: (row.description as string) || undefined,
    type: row.type as string,
    shortCode: row.short_code as string,
    targetUrl: row.target_url as string,
    fgColor: row.fg_color as string,
    bgColor: row.bg_color as string,
    size: row.size as number,
    errorLevel: (row.error_level as string) as "L" | "M" | "Q" | "H",
    template: (row.template as string) || undefined,
    qrName: (row.qr_name as string) || undefined,
    qrSubtext: (row.qr_subtext as string) || undefined,
    brandText: (row.brand_text as string) || undefined,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
    scans: (row.scans as number) || 0,
    userId: row.user_id as string,
  };
}

export function useQRCodes() {
  const { user } = useAuthContext();
  const [qrCodes, setQRCodes] = useState<SavedQRCode[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      setQRCodes([]);
      return;
    }
    setLoading(true);
    supabase
      .from("qr_codes")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .then(({ data, error }) => {
        if (!error && data) {
          setQRCodes(data.map(toCamelCase));
        }
        setLoading(false);
      });
  }, [user]);

  const save = useCallback(
    async (qrData: Omit<SavedQRCode, "id" | "shortCode" | "createdAt" | "updatedAt" | "scans" | "userId">) => {
      // Validate user with server — getUser() verifies the JWT token
      const { data: { user: currentUser }, error: userErr } = await supabase.auth.getUser();
      if (userErr || !currentUser) {
        throw new Error("Oturum geçersiz. Lütfen tekrar giriş yapın.");
      }
      const userId = currentUser.id;

      // Ensure profiles row exists (FK constraint on qr_codes.user_id -> profiles.id)
      const { data: profile } = await supabase
        .from("profiles")
        .select("id")
        .eq("id", userId)
        .maybeSingle();
      if (!profile) {
        const { error: profileErr } = await supabase
          .from("profiles")
          .insert({
            id: userId,
            full_name: currentUser.user_metadata?.full_name || currentUser.email?.split("@")[0] || null,
          });
        if (profileErr) {
          console.error("[useQRCodes save] Profile creation failed:", profileErr);
          throw new Error(`Profil oluşturulamadı: ${profileErr.message}`);
        }
      }

      let shortCode = generateShortCode();
      for (let i = 0; i < 10; i++) {
        const { data: existing } = await supabase
          .from("qr_codes")
          .select("id")
          .eq("short_code", shortCode)
          .maybeSingle();
        if (!existing) break;
        shortCode = generateShortCode();
      }

      const { data, error } = await supabase
        .from("qr_codes")
        .insert({
          user_id: userId,
          name: qrData.name,
          description: qrData.description || null,
          type: qrData.type,
          short_code: shortCode,
          target_url: qrData.targetUrl,
          fg_color: qrData.fgColor,
          bg_color: qrData.bgColor,
          size: qrData.size,
          error_level: qrData.errorLevel,
          template: qrData.template || null,
          qr_name: qrData.qrName || null,
          qr_subtext: qrData.qrSubtext || null,
          brand_text: qrData.brandText || null,
        })
        .select();

      if (error) {
        console.error("[useQRCodes save] Supabase insert error:", error);
        throw new Error(error.message || "Veritabanı kayıt hatası");
      }

      if (!data || data.length === 0) {
        throw new Error("Kayıt oluşturuldu ancak veri alınamadı");
      }

      const saved = toCamelCase(data[0] as Record<string, unknown>);
      setQRCodes((prev) => [saved, ...prev]);
      return saved;
    },
    []
  );

  const update = useCallback(
    async (id: string, changes: Partial<Pick<SavedQRCode, "name" | "description" | "targetUrl">>) => {
      if (!user) throw new Error("Giriş yapmalısınız");
      const updates: Record<string, unknown> = {};
      if (changes.name !== undefined) updates.name = changes.name;
      if (changes.description !== undefined) updates.description = changes.description;
      if (changes.targetUrl !== undefined) updates.target_url = changes.targetUrl;
      const { data, error } = await supabase
        .from("qr_codes")
        .update(updates)
        .eq("id", id)
        .eq("user_id", user.id)
        .select()
        .single();
      if (error) throw error;
      const updated = toCamelCase(data as Record<string, unknown>);
      setQRCodes((prev) => prev.map((qr) => (qr.id === id ? updated : qr)));
      return updated;
    },
    [user]
  );

  const remove = useCallback(
    async (id: string) => {
      if (!user) throw new Error("Giriş yapmalısınız");
      const { error } = await supabase
        .from("qr_codes")
        .delete()
        .eq("id", id)
        .eq("user_id", user.id);
      if (error) throw error;
      setQRCodes((prev) => prev.filter((qr) => qr.id !== id));
    },
    [user]
  );

  return { qrCodes, loading, save, update, remove };
}